/**
 * Central HTTP helpers for **same-origin JSON calls** to `/api/...` from the browser.
 *
 * **Use `apiFetch` / `apiGet` / `apiPost` / …** when:
 * - The request goes to your app’s `/api` routes.
 * - Request body is JSON **or** `FormData` (uploads).
 * - The success response body is JSON (or empty — see below).
 * - **GET/HEAD** calls automatically retry twice on HTTP 429 (short backoff).
 *
 * **Keep raw `fetch` instead** when:
 * - The response is a **blob** (e.g. PDF download).
 * - You use **EventSource** or streaming.
 * - You call **external** URLs (Beds24, Keycloak, …).
 * - You need custom handling of **`Response`** (e.g. read status before committing to JSON).
 */

import { isSessionLostCandidate, notifySessionLost } from '@/lib/session-lost';
import { notifyApiMutation } from '@/lib/sync/cached-get';

function mergeJsonContentType(headers: HeadersInit | undefined): Headers {
  const h = new Headers(headers);
  if (!h.has('Content-Type')) {
    h.set('Content-Type', 'application/json');
  }
  return h;
}

function errorMessageFromBody(data: unknown): string {
  if (!data || typeof data !== 'object') return 'Something went wrong';
  const o = data as Record<string, unknown>;
  if (typeof o.error === 'string' && o.error.length > 0) return o.error;
  if (typeof o.detail === 'string' && o.detail.length > 0) return o.detail;
  if (typeof o.message === 'string' && o.message.length > 0) return o.message;
  return 'Something went wrong';
}

/** Thrown when `!res.ok` or when the response body is not valid JSON (success path). */
export class ApiError extends Error {
  readonly status: number;
  readonly body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Prefer this over `instanceof ApiError` across bundle boundaries. */
export function isApiError(e: unknown): e is ApiError {
  return (
    typeof e === 'object' &&
    e !== null &&
    (e as ApiError).name === 'ApiError' &&
    typeof (e as ApiError).status === 'number'
  );
}

function isFormDataBody(body: RequestInit['body']): boolean {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

/**
 * Single fetch + parse. Used by {@link apiFetch} (which may retry safe methods on 429).
 */
async function apiFetchOnce<T>(url: string, options?: RequestInit): Promise<T> {
  const method = (options?.method ?? 'GET').toUpperCase();
  const formData = isFormDataBody(options?.body);
  const needsJsonContentType =
    ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && !formData;

  const fetchOptions: RequestInit = {
    ...options,
    cache: 'no-store' as RequestCache,
    headers: needsJsonContentType ? mergeJsonContentType(options?.headers) : options?.headers,
  };

  const res = await fetch(url, fetchOptions);
  const text = await res.text();
  const trimmed = text.trim();

  if (!res.ok) {
    let body: unknown;
    let message = 'Something went wrong';
    if (trimmed) {
      try {
        body = JSON.parse(trimmed) as unknown;
        message = errorMessageFromBody(body);
      } catch {
        message = trimmed.length > 500 ? `${trimmed.slice(0, 500)}…` : trimmed;
      }
    } else {
      message = res.statusText || `Request failed (${res.status})`;
    }
    if (res.status === 401 && isSessionLostCandidate(url)) {
      // Sessie weg (verlopen/ingetrokken): de SessionLostGuard bevestigt en stuurt naar login.
      notifySessionLost(url);
    }
    throw new ApiError(message, res.status, body);
  }

  if (!trimmed) {
    return undefined as T;
  }

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    throw new ApiError('Invalid JSON in response', res.status);
  }
}

/**
 * Client-side fetch helper: parses JSON (or empty body as `undefined`), throws `ApiError` on failure.
 * **GET/HEAD:** on HTTP 429, retries up to 2 extra times with short backoff (rate limits).
 */
export type ApiFetchOptions = RequestInit & {
  /**
   * Plan E2: zonder verbinding de mutatie in de offline-outbox zetten en afspelen zodra de browser
   * weer online is. Standaard aan voor PATCH/PUT met een JSON-body; POST/DELETE alleen op verzoek.
   * De aanroeper krijgt dan `{ queued: true }` terug i.p.v. een fout.
   */
  queueOffline?: boolean;
  /** Korte omschrijving voor de outbox-banner. */
  outboxLabel?: string;
};

export type QueuedResponse = { queued: true; outboxId: string };
export function isQueuedResponse(v: unknown): v is QueuedResponse {
  return !!v && typeof v === 'object' && (v as QueuedResponse).queued === true;
}

export async function apiFetch<T>(url: string, options?: ApiFetchOptions): Promise<T> {
  const method = (options?.method ?? 'GET').toUpperCase();
  const allow429Retry = method === 'GET' || method === 'HEAD';
  const maxAttempts = allow429Retry ? 3 : 1;
  const bodyIsJson = typeof options?.body === 'string';
  const queueOffline = options?.queueOffline ?? ((method === 'PATCH' || method === 'PUT') && bodyIsJson);

  let lastErr: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const result = await apiFetchOnce<T>(url, options);
      // Geslaagde mutatie → SWR-caches op verwante paden ongeldig (zie lib/sync/cached-get.ts).
      if (!allow429Retry && typeof window !== 'undefined') notifyApiMutation(url);
      return result;
    } catch (e) {
      lastErr = e;
      if (queueOffline && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') && typeof window !== 'undefined') {
        const { isOfflineError, enqueueOutbox } = await import('@/lib/sync/outbox');
        if (isOfflineError(e)) {
          const entry = enqueueOutbox({ url, method: method as 'POST' | 'PUT' | 'PATCH' | 'DELETE', body: bodyIsJson ? (options!.body as string) : null, label: options?.outboxLabel ?? null });
          return { queued: true, outboxId: entry.id } as unknown as T;
        }
      }
      if (
        allow429Retry &&
        attempt < maxAttempts - 1 &&
        isApiError(e) &&
        e.status === 429
      ) {
        const delayMs = 400 * (attempt + 1);
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }
      throw e;
    }
  }
  throw lastErr;
}

export function apiGet<T>(url: string, options?: Omit<RequestInit, 'body' | 'method'>): Promise<T> {
  return apiFetch<T>(url, { ...options, method: 'GET' });
}

export function apiPost<T>(
  url: string,
  body?: unknown,
  options?: Omit<RequestInit, 'body' | 'method'>,
): Promise<T> {
  return apiFetch<T>(url, {
    ...options,
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiPut<T>(
  url: string,
  body?: unknown,
  options?: Omit<RequestInit, 'body' | 'method'>,
): Promise<T> {
  return apiFetch<T>(url, {
    ...options,
    method: 'PUT',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiPatch<T>(
  url: string,
  body?: unknown,
  options?: Omit<RequestInit, 'body' | 'method'>,
): Promise<T> {
  return apiFetch<T>(url, {
    ...options,
    method: 'PATCH',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiDelete<T>(
  url: string,
  body?: unknown,
  options?: Omit<RequestInit, 'body' | 'method'>,
): Promise<T> {
  return apiFetch<T>(url, {
    ...options,
    method: 'DELETE',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/** POST multipart — do not set `Content-Type` manually; boundary is set by the browser. */
export function apiPostFormData<T>(
  url: string,
  formData: FormData,
  options?: Omit<RequestInit, 'body' | 'method'>,
): Promise<T> {
  return apiFetch<T>(url, { ...options, method: 'POST', body: formData });
}
